import { useRef, useCallback, useEffect } from "react";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { TranscriptionError } from "shared/errors";

const useAzureSpeech = ({
    onRecognizing,
    onRecognized,
    onSynthesized,
    onStartListening,
    onStopListening,
    onStartSynthesizing,
    onStopSynthesizing,
    onError,
}) => {
    const speechConfigRef = useRef(null);
    const recognizerRef = useRef(null);
    const inputAudioConfigRef = useRef(null);

    const synthesizerRef = useRef(null);
    const outputAudioConfigRef = useRef(null);
    const outputAudioStreamRef = useRef(null);

    const onRecognizingRef = useRef(onRecognizing);
    const onRecognizedRef = useRef(onRecognized);
    const onSynthesizedRef = useRef(onSynthesized);
    const onStartListeningRef = useRef(onStartListening);
    const onStopListeningRef = useRef(onStopListening);
    const onStartSynthesizingRef = useRef(onStartSynthesizing);
    const onStopSynthesizingRef = useRef(onStopSynthesizing);

    const transcript = useRef(null);
    const currentSessionRef = useRef(null);

    useEffect(() => {
        onRecognizingRef.current = onRecognizing;
    }, [onRecognizing]);

    useEffect(() => {
        onRecognizedRef.current = onRecognized;
    }, [onRecognized]);

    useEffect(() => {
        onSynthesizedRef.current = onSynthesized;
    }, [onSynthesized]);

    useEffect(() => {
        onStartListeningRef.current = onStartListening;
    }, [onStartListeningRef]);

    useEffect(() => {
        onStopListeningRef.current = onStopListening;
    }, [onStopListeningRef]);

    useEffect(() => {
        onStartSynthesizingRef.current = onStartSynthesizing;
    }, [onStartSynthesizingRef]);

    useEffect(() => {
        onStopSynthesizingRef.current = onStopSynthesizing;
    }, [onStopSynthesizingRef]);

    const updateAuthToken = useCallback((tokenCache) => {
        if (recognizerRef.current) {
            recognizerRef.current.authorizationToken = tokenCache.token;
        }
        
        if (speechConfigRef.current) {
            speechConfigRef.current.authorizationToken = tokenCache.token;
        }

        if(synthesizerRef.current) {
            synthesizerRef.current.authorizationToken = tokenCache.token;
        }
    }, []);

    const initConfigs = useCallback((tokenCache) => {
        if (!speechConfigRef.current) {
            const config = SpeechSDK.SpeechConfig.fromAuthorizationToken(tokenCache.token, tokenCache.region);
            config.enableDictation();
            
            config.setProperty(
                SpeechSDK.PropertyId.Speech_SegmentationSilenceTimeoutMs,
                '1000'
            );
            config.setProperty(
                SpeechSDK.PropertyId.Speech_SegmentationMaximumTimeMs,
                '70000'
            );

            config.setProperty(
                SpeechSDK.PropertyId.Conversation_Initial_Silence_Timeout,
                0
            );

            config.setProperty(
                SpeechSDK.PropertyId.SpeechServiceConnection_RecoMode, 
                "CONVERSATION"
            );

            speechConfigRef.current = config;
        }

        if (!inputAudioConfigRef.current) {
            inputAudioConfigRef.current = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
        }
    }, []);

    const initRecognizer = useCallback((languages = ["en-US"]) => {
        const autoDetectSourceLanguageConfig = SpeechSDK.AutoDetectSourceLanguageConfig.fromLanguages(languages);

        const recognizer = SpeechSDK.SpeechRecognizer.FromConfig(
            speechConfigRef.current, autoDetectSourceLanguageConfig, inputAudioConfigRef.current);

        //TODO: Explore phraseListGrammer for better transcription of specific words
        // const phraseList = SpeechSDK.PhraseListGrammar.fromRecognizer(recognizer);
        // phraseList.addPhrases([
        //     "LYFE",
        //     "AP"
        // ]);

        // phraseList.setWeight(2);

        recognizer.recognizing = (_, e) => {
            if (onRecognizingRef.current) {
                onRecognizingRef.current({
                    language: e.result.language,
                    text: e.result.text
                });
            }
        };

        recognizer.recognized = async (_, e) => {
            if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
                await onRecognizedRef.current({
                    language: e.result.language,
                    text: e.result.text
                }, currentSessionRef.current);
                onStopListeningRef.current?.();
            } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
                console.log("No Match: Speech could not be recognized.");
            }
        };

        recognizer.canceled = (_, e) => {
            console.log("cancelled", e);
            onError?.(new TranscriptionError(e));
            stopListening();
        };

        recognizer.sessionStopped = () => {
            stopListening();
        };

        const recognizerConnection = SpeechSDK.Connection.fromRecognizer(recognizer);
        recognizerConnection.openConnection(
            () => { console.log("Recognizer connected"); },
            (err) => { onError?.(err); },
        );

        recognizerRef.current = recognizer;
    }, []);

    const initSynthesizer = useCallback(() => {
        const stream = SpeechSDK.AudioOutputStream.createPullStream();
        const audioConfig = SpeechSDK.AudioConfig.fromStreamOutput(stream);
        const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfigRef.current, audioConfig);

        synthesizer.synthesisCompleted = (_, e) => {
            onStopSynthesizingRef.current?.();
        };

        synthesizer.synthesisStarted = (_, e) => {
            onStartSynthesizingRef.current?.();
        };

        const synthesizerConnection = SpeechSDK.Connection.fromSynthesizer(synthesizer);
        synthesizerConnection.openConnection(
            () => { console.log("Synthesizer connected"); },
            (err) => { onError?.(err); },
        );

        outputAudioStreamRef.current = stream;
        outputAudioConfigRef.current = audioConfig;
        synthesizerRef.current = synthesizer;
    }, []);

    const initAzureSpeech = useCallback((languages) => {
        initRecognizer(languages);
        initSynthesizer();
    }, [recognizerRef.current, synthesizerRef.current]);

    const stopListening = useCallback(() => {
        return new Promise((resolve, reject) => {
            const recognizer = recognizerRef.current;
            if (!recognizer) {
                return resolve();
            }
            
            transcript.current = null;

            recognizer.stopContinuousRecognitionAsync(
                () => {
                    onStopListeningRef.current?.();
                    resolve();
                },
                (err) => {
                    onError?.(err);
                    onStopListeningRef.current?.();
                    reject(err);
                }
            );
        });
    }, []);

    const disposeRecogniser = useCallback(() => {
        const recognizer = recognizerRef.current;
        
        if (recognizer) {
            if (!recognizer.privDisposed) {
                recognizer.close();
            }
            recognizer.recognizing = undefined;
            recognizer.recognized = undefined;
            recognizer.canceled = undefined;
            recognizer.sessionStopped = undefined;
        }

        recognizerRef.current = null;
    }, []);

    const disposeSynthesizer = useCallback(() => {
        const synthesizer = synthesizerRef.current;
        if (synthesizer) {
            synthesizer.close();
            outputAudioStreamRef.current?.close();
        }

        outputAudioStreamRef.current = null;
        outputAudioConfigRef.current = null;
        synthesizerRef.current = null;
    }, []);

    const disposeAzureSpeech = useCallback(async () => {
        await stopListening();
        disposeRecogniser();
        disposeSynthesizer();
    }, []);

    const checkMicPermission = async () => {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(t => t.stop());
    }

    const startListening = useCallback(async () => {
        const recognizer = recognizerRef.current;
        transcript.current = null;
        currentSessionRef.current = Date.now();

        try {
            await checkMicPermission();
            await recognizer?.startContinuousRecognitionAsync(() => { onStartListeningRef.current?.(); });
        } catch(err) {
            onError?.(err);
        }
    }, [initConfigs, stopListening]);

    const synthesizeText = useCallback(({ text, language, voiceName = "en-US-AndrewMultilingualNeural", prosodyRate = "-10.00%", sessionId = '' }) => {
        return new Promise((resolve, reject) => {
            const ssml = `
                <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${language}">
                    <voice name="${voiceName}">
                        <prosody rate="${prosodyRate}">
                            ${text}
                        </prosody>
                    </voice>
                </speak>
            `;

            synthesizerRef?.current?.speakSsmlAsync(
                ssml,
                async (result) => {
                    if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
                        const blob = new Blob([result.audioData], { type: "audio/wav" });

                        if (onSynthesizedRef.current) {
                            onSynthesizedRef.current(blob, sessionId);
                        }
                    }
                    resolve();
                },
                error => {
                    onError?.(error);
                    reject(error);
                }
            );
        })
    }, []);

    return {
        initConfigs,
        initAzureSpeech,
        initSynthesizer,
        startListening,
        stopListening,
        synthesizeText,
        disposeAzureSpeech,
        updateAuthToken,
        sessionRef: currentSessionRef,
    };
};

export default useAzureSpeech;
